/*
 * The row menu's actions, built once per conversation for every surface that draws message rows —
 * the timeline and the thread pane — so the two cannot drift apart in what "Copy link" or
 * "Mark as unread from here" does.
 *
 * The object is stable for the life of the surface: every row receives it as a prop, and a new
 * identity would re-render three hundred rows for a change in one closure. So the surface's own
 * state setters and the pieces that change (the slug, the people) are read through a ref at call
 * time rather than captured when the object is made.
 *
 * An action that leaves the screen exactly as it was reports as a toast — a clipboard
 * write that silently failed is indistinguishable from one that silently worked — and it reports
 * *once*: the card is its own live region, a completed action a `role="status"` and a refusal a
 * `role="alert"` (core/shell/ToastHost.tsx). For a while a copy also went through the surface's
 * announcer, which said the same sentence a second time and said a failure both politely and
 * assertively.
 *
 * Every report waits for its action. A toast fired beside a request rather than after it tells a
 * reader that something happened on a surface where nothing did.
 */

import { useLayoutEffect, useMemo, useRef } from "react"
import { useToast } from "../../../core/shell/index.ts"
import type { ConversationId } from "../../../core/store/model.ts"
import type { Addressable } from "../../../routes/links.ts"
import { chatDeps } from "../deps.tsx"
import { permalinkUrl } from "../render/permalink.ts"
import type { MessageView } from "../wire/views.ts"
import { copyMessages } from "./copy.ts"
import type { MessageRowActions } from "./MessageRow.tsx"

export interface MessageActionOptions {
	/**
	 * The conversation a permalink is built from. It is the whole address and not the slug alone,
	 * because a direct conversation has no slug and is written `/d/<handle>/p/<seq>`.
	 */
	place: Addressable | undefined
	/** Who wrote what: a copied message is headed by its author's name. */
	users: Record<number, { displayName: string }>
	/** Opens the in-place edit box on this message. */
	edit: (msg: MessageView) => void
	/** Asks before deleting; the surface owns the dialog. */
	remove: (msg: MessageView) => void
	toggleSelect: (msg: MessageView) => void
	/** Absent on a surface that cannot show a thread — the thread pane itself. */
	openThread?: ((msg: MessageView) => void) | undefined
	/** Absent on a surface that cannot show a profile. */
	openProfile?: ((userId: number) => void) | undefined
	/** The reader holds no row in this conversation: it reads, and writes nothing (`MessageRowActions`). */
	readOnly?: boolean | undefined
}

export interface MessageActions extends MessageRowActions {
	/** Copies several messages at once, as markdown — the selection bar's action. */
	copyMany: (list: readonly MessageView[]) => void
}

export function useMessageActions(
	conv: ConversationId,
	opts: MessageActionOptions,
): MessageActions {
	const latest = useRef(opts)
	useLayoutEffect(() => {
		latest.current = opts
	})
	const deps = chatDeps()
	const toast = useToast()
	// The two presence checks are the only thing read when the object is built; everything else
	// goes through the ref at call time.
	const withThread = opts.openThread !== undefined
	const withProfile = opts.openProfile !== undefined
	const readOnly = opts.readOnly === true

	return useMemo<MessageActions>(() => {
		const copy = async (list: readonly MessageView[]) => {
			const { place, users } = latest.current
			const ok = await copyMessages(list, {
				authorName: (userId) => users[userId]?.displayName ?? "",
				// A conversation with no address contributes no permalink line rather than a broken
				// one; `buildTranscript` leaves the empty string out of the head.
				permalink: (seq) => (place === undefined ? "" : (permalinkUrl(place, seq) ?? "")),
			})
			toast({
				kind: ok ? "success" : "error",
				key: ok ? "chat.message.copied" : "chat.message.copyFailed",
			})
		}
		const actions: MessageActions = {
			copyMany(list) {
				void copy(list)
			},
			copyLink(msg) {
				const { place } = latest.current
				const url = place === undefined ? null : permalinkUrl(place, msg.seq)
				const failed = () => toast({ kind: "error", key: "chat.message.copyFailed" })
				if (url === null) {
					failed()
					return
				}
				void navigator.clipboard
					?.writeText(url)
					.then(() => toast({ kind: "success", key: "chat.message.copied" }))
					.catch(failed)
			},
			copyMarkdown(msg) {
				void copy([msg])
			},
			edit(msg) {
				latest.current.edit(msg)
			},
			remove(msg) {
				latest.current.remove(msg)
			},
			markUnreadFrom(msg) {
				// A surface whose dependencies do not offer the call says nothing rather than
				// reporting a cursor move that never left the tab.
				const mark = deps.markUnreadFrom
				if (mark === undefined) return
				void mark({ conv, seq: msg.seq })
					.then(() => toast({ kind: "success", key: "chat.message.markedUnread" }))
					.catch(() => toast({ kind: "error", key: "chat.message.markUnreadFailed" }))
			},
			toggleSelect(msg) {
				latest.current.toggleSelect(msg)
			},
		}
		// `MessageRow` draws an affordance only when its caller exists, so the two optional ones are
		// present exactly when the surface offered them — not as a key holding `undefined`.
		if (withThread) actions.openThread = (msg) => latest.current.openThread?.(msg)
		if (withProfile) actions.openProfile = (id) => latest.current.openProfile?.(id)
		if (readOnly) actions.readOnly = true
		return actions
	}, [conv, deps, toast, withThread, withProfile, readOnly])
}
